import { z } from 'zod/v4';
import {
  type CreatedQuestionComment,
  CreatedQuestionCommentSchema,
} from './question-comment-create.js';

/**
 * The comment, and the question and answer request it must belong to.
 *
 * The legacy route addressed the comment by its id alone
 * (`PATCH /v1/questions/comments/:id`) and never saw the other two ids. They are
 * on the path here so the edit addresses the same collection the list read and
 * the create do — see `ListQuestionCommentsParamsSchema` and
 * `CreateQuestionCommentParamsSchema`, whose paths this extends by one segment.
 *
 * That makes the two extra ids load-bearing rather than decorative: the service
 * verifies the comment really is on that question and that request, and answers
 * 404 otherwise. This is a deliberate tightening of the legacy behaviour, which
 * would happily edit a comment through any question/request pair.
 *
 * The 64-character cap is the legacy body schema's, applied to all three for
 * consistency. Comment ids are 11-character `shortId`s, so it cannot turn a real
 * lookup into a 400.
 */
export const UpdateQuestionCommentParamsSchema = z.object({
  questionId: z.string().min(1).max(64),
  requestId: z.string().min(1).max(64),
  commentId: z.string().min(1).max(64),
});

export type UpdateQuestionCommentParams = z.infer<
  typeof UpdateQuestionCommentParamsSchema
>;

/**
 * Just the new comment text — the ids live on the path.
 *
 * `min(1)` and `max(4000)` are the legacy body schema's, and that schema was
 * `additionalProperties: false`, so a strict object here is parity rather than a
 * tightening.
 */
export const UpdateQuestionCommentBodySchema = z.object({
  questionComment: z.string().min(1).max(4000),
});

export type UpdateQuestionCommentBody = z.infer<
  typeof UpdateQuestionCommentBodySchema
>;

/**
 * The comment row as the UPDATE returns it.
 *
 * Aliased rather than re-declared: the legacy service built its response by
 * calling `queries.getById` after the UPDATE, which is the same `getById`
 * projection the create's `RETURNING` reproduces. One definition cannot drift
 * from the other, and if the projection ever changes it changes in one place.
 *
 * As on create, **the comment author is `updatedBy*`, not `createdBy*`** — an
 * edit rewrites exactly those columns, which is what both frontends render.
 */
export const UpdatedQuestionCommentSchema = CreatedQuestionCommentSchema;

export type UpdatedQuestionComment = CreatedQuestionComment;

/**
 * `{ metadata, data }` — `reply.apiSuccess(comment)`, the convention the migrated
 * create route on this collection answers in.
 *
 * Two deliberate changes from the legacy route, which sent a bare top-level
 * object with a hard-coded `201`: the envelope, and the status. A `201` on a
 * PATCH was always wrong, and the declared `200` schema was dead code because no
 * `201` schema existed for Fastify to serialise against. Both frontends branched
 * on `status === 201` and are updated in the same change.
 */
export const UpdateQuestionCommentResponseSchema = z.object({
  metadata: z.object({ success: z.boolean() }),
  data: UpdatedQuestionCommentSchema,
});

export type UpdateQuestionCommentResponse = z.infer<
  typeof UpdateQuestionCommentResponseSchema
>;
